import type { InvoiceDirection, InvoiceListItem } from '@tandemone/types'
import type { ColumnDef } from '@tanstack/react-table'

import { DataTableColumnHeader } from '@/components/ui/data-table-column-header'
import { formatDate } from '@/lib/format-date'
import { cn } from '@/lib/utils'

import { InvoiceRowActions } from './invoice-row-actions'
import { InvoiceStatusBadge } from './invoice-status-badge'
import { formatMoney, netTermDays } from './utils'

/** the counterparty column flips meaning with direction: on Sent it's who you
 * billed (your customer), on Received it's who billed you (your supplier).
 */
export function invoiceColumns(
  direction: InvoiceDirection
): ColumnDef<InvoiceListItem>[] {
  return [
    {
      accessorKey: 'invoiceNumber',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title='Invoice' />
      ),
      meta: { width: '110px' },
      cell: ({ row }) => (
        <span className='text-[13px] font-medium text-ink'>
          {row.original.invoiceNumber}
        </span>
      )
    },
    {
      accessorKey: 'counterpartyName',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={direction === 'received' ? 'Supplier' : 'Customer'}
        />
      ),
      meta: { width: '24%' },
      cell: ({ row }) => (
        <span className='block truncate text-[13px] text-ink-soft'>
          {row.original.counterpartyName}
        </span>
      )
    },
    {
      accessorKey: 'totalAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title='Amount' />
      ),
      meta: { width: '120px' },
      cell: ({ row }) => (
        <span className='text-[13px] font-medium text-ink'>
          {formatMoney(row.original.totalAmount, row.original.currency)}
        </span>
      )
    },
    {
      accessorKey: 'issuedAt',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title='Issued' />
      ),
      meta: { width: '120px' },
      cell: ({ row }) => (
        <span className='text-[13px] text-ink-soft'>
          {formatDate(row.original.issuedAt)}
        </span>
      )
    },
    {
      accessorKey: 'dueAt',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title='Due' />
      ),
      meta: { width: '120px' },
      cell: ({ row }) => (
        <span
          className={cn(
            'text-[13px]',
            row.original.isOverdue
              ? 'font-medium text-rose-600 dark:text-rose-400'
              : 'text-ink-soft'
          )}
        >
          {formatDate(row.original.dueAt)}
        </span>
      )
    },
    {
      id: 'netTerms',
      header: 'Net terms',
      enableSorting: false,
      meta: { width: '90px' },
      cell: ({ row }) => {
        const days = netTermDays(row.original.issuedAt, row.original.dueAt)
        return (
          <span className='text-[13px] text-ink-muted'>
            {days === null ? '—' : `Net ${days}`}
          </span>
        )
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      enableSorting: false,
      meta: { width: '110px' },
      cell: ({ row }) => (
        <InvoiceStatusBadge
          status={row.original.status}
          isOverdue={row.original.isOverdue}
          direction={direction}
        />
      )
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      meta: { width: '56px', excludeFromRowLink: true },
      cell: ({ row }) => <InvoiceRowActions invoice={row.original} />
    }
  ]
}
